/* FILE: ./frontend/src/features/canada/pages/RfqInviteTeaser.jsx */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

export default function RfqInviteTeaser() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [rfqData, setRfqData] = useState(null);

  // Quote Form State
  const [quoteMode, setQuoteMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [quoteData, setQuoteData] = useState({ price: '', leadTime: '', validity: '30 Days', incoterms: 'EXW (Ex Works)', conditions: '' });

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing invitation token.");
      setLoading(false);
      return;
    }

    const fetchInviteAndRfq = async () => {
      try {
        const { data: invite, error: inviteError } = await supabase
          .from('rfq_invitations')
          .select('*')
          .eq('secure_token', token)
          .maybeSingle();

        if (inviteError || !invite) throw new Error("Invitation not found or expired.");
        setInviteData(invite);

        if (invite.status === 'sent') {
            await supabase.from('rfq_invitations').update({ status: 'viewed' }).eq('id', invite.id);
        }

        const { data: rfq, error: rfqError } = await supabase
          .from('rfqs')
          .select('id, hs_code, destination_province, quantity, uom, technical_specs, created_at')
          .eq('id', invite.rfq_id)
          .maybeSingle();

        if (rfqError || !rfq) throw new Error("The associated RFQ is no longer available.");
        setRfqData(rfq);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInviteAndRfq();
  }, [token]);

  // ⚡ ACTION 1: Chat / Ask for Details (Routes to Message Center)
  const handleChatClick = () => {
    const targetDestination = `/inbox?tab=selling&rfq=${rfqData.id}&mode=chat`;
    if (user) {
        navigate(targetDestination);
    } else {
        sessionStorage.setItem('returnTo', targetDestination);
        navigate('/login', { state: { returnTo: targetDestination } });
    }
  };

  // ⚡ ACTION 2: Inline Quote (Stays on page or returns here after login)
  const handleQuoteClick = () => {
    if (user) {
        setQuoteMode(true);
    } else {
        const targetDestination = location.pathname + location.search;
        sessionStorage.setItem('returnTo', targetDestination);
        navigate('/login', { state: { returnTo: targetDestination } });
    }
  };

  const submitQuote = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
          await axios.post(`${API_BASE}/inquiries/quote`, {
              rfq_id: rfqData.id,
              price: quoteData.price,
              leadTime: quoteData.leadTime,
              validity: quoteData.validity,
              incoterms: quoteData.incoterms,
              conditions: quoteData.conditions
          }, { headers: { 'x-user-id': user.id }});
          
          await supabase.from('rfq_invitations').update({ status: 'registered' }).eq('id', inviteData.id);
          setQuoteSuccess(true);
      } catch (err) {
          alert(err.response?.data?.error || err.message);
      } finally {
          setSubmitting(false);
      }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-md max-w-md text-center border border-gray-200">
            <span className="text-4xl mb-4 block">🚫</span>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invitation Invalid</h2>
            <p className="text-gray-600 text-sm mb-6">{error}</p>
            <Link to="/" className="text-blue-600 font-bold hover:underline">Return to Homepage</Link>
        </div>
      </div>
    );
  }

  if (quoteSuccess) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="bg-white p-10 rounded-2xl shadow-xl max-w-lg text-center border border-gray-100">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">✓</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Submitted Successfully!</h2>
                <p className="text-gray-600 text-sm mb-8">The buyer has been notified. You can track this negotiation in your Inbox.</p>
                <Link to="/inbox" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors">Go to Inbox</Link>
            </div>
        </div>
      );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto">
        <div className="bg-blue-600 text-white p-4 rounded-t-2xl shadow-md flex items-center gap-3">
            <span className="text-2xl">🔥</span>
            <div>
                <h1 className="font-bold text-lg">Verified Buyer Lead</h1>
                <p className="text-sm text-blue-100">You have been exclusively invited to bid on this request.</p>
            </div>
        </div>

        <div className="bg-white p-8 rounded-b-2xl shadow-xl border border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-100 pb-6 mb-6">
                <div>
                    <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Target Material (HS Code)</p>
                    <h2 className="text-3xl font-extrabold text-gray-900">{rfqData.hs_code}</h2>
                </div>
                <div className="mt-4 md:mt-0 text-left md:text-right">
                    <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Requested Volume</p>
                    <h2 className="text-3xl font-extrabold text-blue-600">{Number(rfqData.quantity).toLocaleString()} <span className="text-lg text-blue-400">{rfqData.uom}</span></h2>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><span>📍</span> Delivery Destination</h3>
                    <p className="text-xl font-bold text-gray-900">{rfqData.destination_province}, Canada</p>
                    <p className="text-xs text-gray-500 mt-2">CFTA inter-provincial compliance rules may apply.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><span>🛡️</span> Buyer Status</h3>
                    <p className="text-xl font-bold text-green-600 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                        Verified Business
                    </p>
                    <p className="text-xs text-gray-500 mt-2">Identity hidden until quote is accepted.</p>
                </div>
            </div>

            <div className="mb-8">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Technical Specifications</h3>
                <div className="bg-gray-50 p-4 rounded border border-gray-200 whitespace-pre-wrap text-sm text-gray-700">
                    {rfqData.technical_specs || "Standard commercial grade requested."}
                </div>
            </div>

            <div className="border-t border-gray-100 pt-8">
                {quoteMode ? (
                    <form onSubmit={submitQuote} className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl animate-fade-in">
                        <h3 className="text-lg font-bold text-yellow-900 mb-4">Submit Official Quote</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                                <label className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">Price (CAD)</label>
                                <input type="number" required min="0" step="0.01" className="w-full border-yellow-300 rounded p-2 text-sm" placeholder="0.00" value={quoteData.price} onChange={e => setQuoteData({...quoteData, price: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">Lead Time</label>
                                <input type="text" required className="w-full border-yellow-300 rounded p-2 text-sm" placeholder="e.g. 4 Weeks" value={quoteData.leadTime} onChange={e => setQuoteData({...quoteData, leadTime: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">Valid For</label>
                                <select className="w-full border-yellow-300 rounded p-2 text-sm bg-white" value={quoteData.validity} onChange={e => setQuoteData({...quoteData, validity: e.target.value})}>
                                    <option>7 Days</option><option>14 Days</option><option>30 Days</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">Incoterms</label>
                                <select className="w-full border-yellow-300 rounded p-2 text-sm bg-white" value={quoteData.incoterms} onChange={e => setQuoteData({...quoteData, incoterms: e.target.value})}>
                                    <option>EXW (Ex Works)</option><option>FCA (Free Carrier)</option><option>DAP (Delivered at Place)</option>
                                </select>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">Additional Conditions</label>
                            <textarea rows="2" className="w-full border-yellow-300 rounded p-2 text-sm resize-none" placeholder="Taxes excluded. Net 30 days..." value={quoteData.conditions} onChange={e => setQuoteData({...quoteData, conditions: e.target.value})} />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button type="button" onClick={() => setQuoteMode(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
                            <button type="submit" disabled={submitting} className="bg-yellow-600 text-white px-6 py-2 rounded font-bold hover:bg-yellow-700 shadow-sm disabled:opacity-50">
                                {submitting ? 'Sending...' : 'Send Quote to Buyer'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="text-center">
                        {!user && (
                            <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-lg mb-6 text-sm font-medium">
                                To protect our buyers, you must verify your business by creating a free Tariffbeater profile before responding to this request.
                            </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button 
                                onClick={handleChatClick}
                                className="w-full sm:w-auto px-8 py-4 bg-white border-2 border-slate-800 text-slate-800 text-lg font-bold rounded-xl hover:bg-slate-50 transition-colors shadow-sm"
                            >
                                {user ? "Ask for Details" : "Login to Ask Details"}
                            </button>

                            <button 
                                onClick={handleQuoteClick}
                                className="w-full md:w-auto px-8 py-4 bg-gray-900 text-white text-lg font-bold rounded-xl shadow-lg hover:bg-gray-800 hover:shadow-xl transition-all transform hover:-translate-y-0.5"
                            >
                                {user ? "Submit Official Quote" : "Login to Quote"}
                            </button>
                        </div>

                        {!user && <p className="text-xs text-gray-400 mt-4">Takes less than 2 minutes. 100% Free.</p>}
                    </div>
                )}
            </div>

        </div>
      </div>
    </div>
  );
}